'use client'

import { useState } from 'react'
import { useSession } from 'next-auth/react'
import { useSearchParams } from 'next/navigation'
import type { StreamEvent } from '@langchain/core/tracers/log_stream'

import SignInForm from '@/components/SignIn'
import Projects from '@/components/Project/List'
import ProjectSetupForm from '@/components/Project/New'
import Animate from '@/components/Animate'
import { StatefulChatInput } from '@/components/Chat'
import Button from '@/components/ui/Button'
import { t } from '@/lib/i18n'
import styles from './page.module.css'
import Spinner from '@/components/Spinner'
import { receiveStream } from '@/lib/stream/client'
import { useChat, useIssues } from '@/lib/state'
import { ThreadCard } from '@/components/Thread'
import { Toast } from '@base-ui-components/react/toast'

function ChatPage() {
  const searchParams = useSearchParams()
  const toastManager = Toast.useToastManager()
  const showLoginForm = searchParams.get('login')
  const threadId = searchParams.get('threadId')
  const setup = searchParams.get('setup')
  const projectName = searchParams.get('projectName')
  const issueNumber = searchParams.get('issueNumber')
  const { data: session, status, update } = useSession()
  const [loading, setLoading] = useState(false)
  const { messages, setMessages, setIsStreaming, addStreamEvent } = useChat()
  const project = session?.user?.projects.find(p => p.name === projectName)
  const thread = project?.threads.find(t => t.id === threadId)
  const { issues, issuesLoading } = useIssues()

  const onChatStream = (event: StreamEvent) => {
    const key =
      event.name === 'ChatOllama'
        ? event.name + ':' + event.metadata.langgraph_step
        : event.name
    // console.log(event)
    setIsStreaming(true)
    addStreamEvent(event, thread?.id as string, key)
  }

  const doneCallback = (_streamEvents: StreamEvent[]) => {
    setIsStreaming(false)
  }

  // old:
  // const doneCallback = (_streamEvents: StreamEvent[]) => {
  // setStreamEvents([])
  // setMessages(_messages => [
  //   ..._messages,
  //   {
  //     type: 'ai',
  //     content: _streamEvents
  //       .map(event => event.data.chunk.kwargs.content)
  //       .join('')
  //   }
  // ])
  // setLoading(false)
  // }

  const handleSendMessage = async (input: string) => {
    if (session == null || !session.user.id) {
      const params = new URLSearchParams(searchParams.toString())
      window.history.pushState(null, '', `?${params.toString()}`)
      // enqueueSnackbar('Please login to continue')
      return
    }
    if (!threadId) {
      // enqueueSnackbar('Please wait for the page to load.')
      return
    }

    const _messages = [
      ...messages,
      {
        type: 'human',
        streamEvents: [],
        content: input
      }
    ]
    setMessages([
      ..._messages,
      {
        type: 'ai',
        streamEvents: []
      }
    ])

    setLoading(true)

    receiveStream({
      body: {
        input,
        threadId,
        graphName: 'supervisor'
      },
      onChatStream,
      doneCallback,
      update,
      toastManager
    })
  }

  return (
    <div className='h-[40vh]'>
      <Projects />
      {!issues?.length && issuesLoading.all ? (
        <Spinner className='h-6 w-6' />
      ) : (
        issues.length > 0 && (
          <>
            <h2 className='text-left ml-4 mb-4 text-xl'>Issues</h2>
            {issues.map(i => (
              <button
                key={i.number}
                className='text-white bg-black m-1 p-2'
                disabled={issuesLoading[i.number]}
                onClick={() => {
                  const params = new URLSearchParams(searchParams)
                  params.get('issueNumber') === i.number.toString()
                    ? params.delete('issueNumber')
                    : params.set('issueNumber', i.number.toString())
                  window.history.pushState(null, '', `?${params.toString()}`)
                  // solveIssue(i.number)
                }}
              >
                {issuesLoading[i.number] ? (
                  <Spinner white className='h-5 w-5' />
                ) : (
                  i.number
                )}
              </button>
            ))}
            <button
              className='text-white bg-black p-2 ml-2'
              onClick={() => {
                const params = new URLSearchParams(searchParams)
                if (searchParams.get('issueNumber') === 'new') {
                  params.delete('issueNumber')
                } else {
                  params.delete('threadId')
                  params.set('issueNumber', 'new')
                }
                window.history.pushState(null, '', `?${params.toString()}`)
                // createNewThread({
                //   projectId: project?.id as string
                // }).then(thread => {
                //   if (thread) {
                //     const params = new URLSearchParams(searchParams)
                //     params.set('threadId', thread.id)
                //     window.history.pushState(null, '', `?${params.toString()}`)
                //   }
                // })
              }}
            >
              {issueNumber === 'new' ? 'x' : '+'}
            </button>
          </>
        )
      )}
      {!showLoginForm && !setup && (issueNumber || thread) && (
        <Animate visible>
          <StatefulChatInput
            loading={loading}
            onSubmit={handleSendMessage}
            //   onStop={thread.stop}
          />
        </Animate>
      )}
      {project?.threads?.length != null && project?.threads.length > 0 && (
        <>
          <h2 className='text-left ml-4 my-4 text-xl'>Tasks</h2>
          {project?.threads
            .sort(
              (a, b) =>
                new Date(b.createdAt).getTime() -
                new Date(a.createdAt).getTime()
            )
            .map(thread => (
              <ThreadCard key={thread.id} thread={thread} />
            ))}
        </>
      )}
      <div className='flex flex-col gap-2 justify-center items-center mb-4'>
        {status === 'loading' && (
          <Animate delay={800}>
            <Spinner className='h-6 w-6' />
          </Animate>
        )}
        {showLoginForm && status === 'unauthenticated' && (
          <Animate visible>
            <SignInForm />
          </Animate>
        )}
        {status === 'authenticated' && (
          <>
            {setup === '0' && (
              <Animate visible>
                <div className='text-lg mb-12'>
                  {t("You haven't created any project yet.")}
                </div>
                <div className='flex justify-center mt-3'>
                  <Button
                    className={styles.button}
                    onClick={() => {
                      const params = new URLSearchParams(
                        searchParams.toString()
                      )
                      params.set('setup', '1')
                      window.history.pushState(
                        null,
                        '',
                        `?${params.toString()}`
                      )
                    }}
                  >
                    {t('Setup new project ✨')}
                  </Button>
                </div>
              </Animate>
            )}
            {setup === '1' && (
              <Animate visible>
                <ProjectSetupForm />
              </Animate>
            )}
          </>
        )}
      </div>
    </div>
  )
}

export default ChatPage
